/**
 * TradeStack - Receipts Page (with OCR scanning)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Receipts.css';

function Receipts() {
  const { config, formatCurrency, formatDate } = useConfig();
  const fileInputRef = useRef(null);

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filter, setFilter] = useState('unreviewed');

  useEffect(() => {
    loadReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const response = await api.getReceipts({
        isReviewed: filter === 'reviewed' ? true : filter === 'unreviewed' ? false : undefined,
        limit: 50
      });
      setReceipts(response.data.receipts || []);
    } catch (err) {
      setError('Failed to load receipts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('receipt', file);

        await api.uploadReceipt(formData);
        setUploadProgress(((i + 1) / files.length) * 100);
      }

      // Reload receipts after upload
      loadReceipts();
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload receipt(s)');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const markAsReviewed = async (receiptId) => {
    try {
      await api.updateReceipt(receiptId, { isReviewed: true });
      setReceipts(receipts.map(r =>
        r.id === receiptId ? { ...r, is_reviewed: true } : r
      ));
    } catch (err) {
      console.error('Failed to mark as reviewed:', err);
    }
  };

  const createExpenseFromReceipt = async (receipt) => {
    try {
      await api.createExpenseFromReceipt(receipt.id);
      markAsReviewed(receipt.id);
    } catch (err) {
      console.error('Failed to create expense:', err);
    }
  };

  const getCategoryName = (categoryId) => {
    const categories = config?.expenseCategories || [];
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId || 'Uncategorized';
  };

  return (
    <div className="receipts-page">
      <div className="page-header">
        <h1>Receipt Scanner</h1>
        <button
          className="btn btn-primary"
          onClick={handleFileSelect}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : '+ Scan Receipt'}
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          multiple
          style={{ display: 'none' }}
        />
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span>{Math.round(uploadProgress)}% uploaded</span>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`tab-btn ${filter === 'unreviewed' ? 'active' : ''}`}
          onClick={() => setFilter('unreviewed')}
        >
          Needs Review
        </button>
        <button
          className={`tab-btn ${filter === 'reviewed' ? 'active' : ''}`}
          onClick={() => setFilter('reviewed')}
        >
          Reviewed
        </button>
        <button
          className={`tab-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
      </div>

      {/* Receipts Grid */}
      {loading ? (
        <div className="loading-state">Loading receipts...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : receipts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📷</div>
          <h3>
            {filter === 'unreviewed' ? 'All caught up!' : 'No receipts found'}
          </h3>
          <p>
            {filter === 'unreviewed'
              ? 'All your receipts have been reviewed'
              : 'Scan your first receipt to get started'}
          </p>
          <button className="btn btn-primary" onClick={handleFileSelect}>
            Scan Receipt
          </button>
        </div>
      ) : (
        <div className="receipts-grid">
          {receipts.map(receipt => (
            <div key={receipt.id} className={`receipt-card ${receipt.is_reviewed ? 'reviewed' : ''}`}>
              <div className="receipt-image">
                {receipt.image_url ? (
                  <img src={receipt.image_url} alt="Receipt" />
                ) : (
                  <div className="placeholder-image">📄</div>
                )}
                {receipt.ocr_status === 'processing' && (
                  <div className="processing-overlay">
                    <span>Processing...</span>
                  </div>
                )}
              </div>
              <div className="receipt-details">
                <div className="receipt-header">
                  <span className="receipt-vendor">{receipt.vendor || 'Unknown Vendor'}</span>
                  <span className="receipt-amount">{formatCurrency(receipt.amount || 0)}</span>
                </div>
                <div className="receipt-meta">
                  <span className="receipt-date">
                    {receipt.receipt_date ? formatDate(receipt.receipt_date) : 'No date'}
                  </span>
                  <span className="receipt-category">{getCategoryName(receipt.category)}</span>
                </div>
                {receipt.ocr_confidence && (
                  <div className="confidence-bar">
                    <div
                      className="confidence-fill"
                      style={{ width: `${receipt.ocr_confidence}%` }}
                    />
                    <span>{receipt.ocr_confidence}% confidence</span>
                  </div>
                )}
                {!receipt.is_reviewed && (
                  <div className="receipt-actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => createExpenseFromReceipt(receipt)}
                    >
                      Create Expense
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => markAsReviewed(receipt.id)}
                    >
                      Mark Reviewed
                    </button>
                  </div>
                )}
                {receipt.expense_id && (
                  <Link to={`/expenses/${receipt.expense_id}`} className="linked-expense">
                    Linked to expense
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Receipts;
